// =========================================================================
// SVG renderer (pure)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「線質差カタログ」
//   §「合流幾何」
//   §「アニメーション」
//   §「レイアウト (1D 円環)」
//   §「Subagent W1-W7」 W3 行
//
// 担当: W3 (pure renderer)
//
// このモジュールは controller を呼ばない。BoardViewModel を受け取って
// SVG を組み立て、ユーザー入力 (辺タップ / ノードタップ) を CanvasEvent と
// して onEvent listener へ発火するだけ。アニメ中は入力をロックする。
//
// 触ってはいけないファイル (cross-owner):
//   - game/controller.ts (W5)
//   - game/edge-interaction.ts, preview.ts, confirm-modal.ts, turn-indicator.ts (W4)
//   - catalog/manifest.ts (W2 確定済)
// =========================================================================

import type { BoardViewModel } from '../catalog/manifest.js';
import { circleLayout } from '../layout/circle.js';

// -------------------------------------------------------------------------
// 公開型
// -------------------------------------------------------------------------

export type CanvasEvent =
  | { readonly type: 'edgeTap'; readonly edgeId: number }
  | { readonly type: 'nodeTap'; readonly nodeId: number };

export interface CanvasHandle {
  /** 新しい ViewModel を流し込む。controller 側で state が更新されるたびに呼ぶ */
  update(viewModel: BoardViewModel): void;
  /** DOM/listener を全て破棄する */
  destroy(): void;
  /** ユーザー入力 listener を登録する (複数可) */
  onEvent(listener: (event: CanvasEvent) => void): void;
}

// -------------------------------------------------------------------------
// 内部定数 (spec の線質差カタログを数値化)
// -------------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg';

/** spec 「ノード」 半径 28px / 終端 56px */
const NODE_RADIUS = 28;
const TERMINAL_NODE_RADIUS = 56;

/** spec 「アニメーション」 timing。styles.css の --t-* と揃える */
const T_FAST = 150; // 辺消費 / 入力ロック最短
const T_MID = 250; // 合流ベジエ
const T_OVERLAY = 600; // 勝利フェードイン (renderer は使わないが定数として置く)

/**
 * history opacity decay の境界手数。
 * spec: 「直近 N 手 = 30% / N 手以上前 = 12%」
 * MVP では N = 3 (直近 3 手は手の流れが見え、それ以前は経年フェード)。
 */
const HISTORY_RECENT_WINDOW = 3;

/** SVG viewBox の論理サイズ (CSS px と揃える)。実描画は container サイズに stretch */
const DEFAULT_VIEW_W = 360;
const DEFAULT_VIEW_H = 360;

// -------------------------------------------------------------------------
// SVG ユーティリティ
// -------------------------------------------------------------------------

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, String(v));
    }
  }
  return el;
}

/**
 * 2 ノード間 (sx,sy) → (dx,dy) の中点を、両ノードを結ぶ法線方向に
 * (ノード径ぶん) 膨らませた制御点を返す。
 *
 * spec 「合流幾何」:
 *   「2 ノードのちょうど中点を通る、ノード径ぶんだけ膨らんだ曲線」
 *
 * 1D 円環の chord でも、外側へ少しふくらませることで「滑らかな収束」 を
 * 表現する。直線の場合は法線方向に同じだけずらす (両端のノードは円の上)。
 */
function bezierBulgeControl(
  sx: number,
  sy: number,
  dx: number,
  dy: number,
  bulge: number,
): { cx: number; cy: number } {
  const mx = (sx + dx) / 2;
  const my = (sy + dy) / 2;
  const ex = dx - sx;
  const ey = dy - sy;
  const len = Math.hypot(ex, ey) || 1;
  // 法線 (左 90 度回転) 単位ベクトル
  const nx = -ey / len;
  const ny = ex / len;
  return { cx: mx + nx * bulge, cy: my + ny * bulge };
}

// -------------------------------------------------------------------------
// レイアウト解決
// -------------------------------------------------------------------------

interface ResolvedPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * BoardViewModel.nodes は既に x, y を持っているはず (controller が解決)。
 * 万一座標が NaN/未設定の場合 (= controller が layoutHints も渡せなかった等
 * のエッジケース) のために、ここでも circleLayout に fallback する。
 */
function resolveNodePositions(
  viewModel: BoardViewModel,
  viewW: number,
  viewH: number,
): Map<number, ResolvedPosition> {
  const m = new Map<number, ResolvedPosition>();
  const needsFallback: number[] = [];

  for (const node of viewModel.nodes) {
    if (
      typeof node.x === 'number' &&
      typeof node.y === 'number' &&
      Number.isFinite(node.x) &&
      Number.isFinite(node.y)
    ) {
      m.set(node.id, { x: node.x, y: node.y });
    } else {
      needsFallback.push(node.id);
    }
  }

  if (needsFallback.length > 0) {
    const fb = circleLayout(needsFallback, viewW, viewH);
    for (const id of needsFallback) {
      const p = fb[id];
      if (p) m.set(id, p);
    }
  }
  return m;
}

// -------------------------------------------------------------------------
// 描画モデル → SVG primitives
// -------------------------------------------------------------------------

interface EdgeStyle {
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly dashArray: string | null;
  readonly opacity: number;
  readonly glow: 'none' | 'soft' | 'strong';
}

/** spec 線質差カタログを数値/CSS 変数に変換 */
function activeEdgeStyle(highlight: 'none' | 'tappable' | 'selected'): EdgeStyle {
  switch (highlight) {
    case 'tappable':
      return {
        stroke: 'var(--accent)',
        strokeWidth: 2,
        dashArray: null,
        opacity: 1,
        glow: 'soft',
      };
    case 'selected':
      return {
        stroke: 'var(--accent-strong)',
        strokeWidth: 2,
        dashArray: null,
        opacity: 1,
        glow: 'strong',
      };
    case 'none':
    default:
      return {
        stroke: 'var(--accent-dim)',
        strokeWidth: 1.5,
        dashArray: null,
        opacity: 1,
        glow: 'none',
      };
  }
}

/**
 * history edge の opacity decay。
 * spec: 「直近 N 手」 = 30% / 「N 手以上前」 = 12%。
 * `age` (= 何手前か) を境界 HISTORY_RECENT_WINDOW で 2 段に切る。
 */
function historyEdgeStyle(age: number): EdgeStyle {
  const opacity = age <= HISTORY_RECENT_WINDOW ? 0.3 : 0.12;
  return {
    stroke: 'var(--text-dim)',
    strokeWidth: 1,
    dashArray: '4 4',
    opacity,
    glow: 'none',
  };
}

// -------------------------------------------------------------------------
// メイン: renderBoard
// -------------------------------------------------------------------------

export function renderBoard(
  container: HTMLElement,
  initialViewModel: BoardViewModel,
): CanvasHandle {
  // ---- 状態 ----
  const listeners: Array<(event: CanvasEvent) => void> = [];
  /** アニメ中は入力をロック (spec §「入力ロック」) */
  let inputLocked = false;
  /** destroy 後に late update が来ても無視するためのフラグ */
  let destroyed = false;
  /** 前フレームの ViewModel (合流アニメ判定 / fade-out に使う) */
  let lastViewModel: BoardViewModel = initialViewModel;

  // ---- container 整備 ----
  container.classList.add('nagi-canvas-root');
  container.style.position = container.style.position || 'relative';
  container.innerHTML = '';

  // ---- SVG ルート ----
  const svg = svgEl('svg', {
    xmlns: SVG_NS,
    viewBox: `0 0 ${DEFAULT_VIEW_W} ${DEFAULT_VIEW_H}`,
    width: '100%',
    height: '100%',
    preserveAspectRatio: 'xMidYMid meet',
    'data-canvas-root': '1',
  });
  svg.style.display = 'block';
  svg.style.touchAction = 'manipulation';

  // defs: glow フィルタ (active 辺の soft/strong 用)
  const defs = svgEl('defs');
  // soft glow
  defs.appendChild(buildGlowFilter('nagi-glow-soft', 1.2));
  // strong glow
  defs.appendChild(buildGlowFilter('nagi-glow-strong', 2.5));
  // 終端ノードの放射 glow
  defs.appendChild(buildGlowFilter('nagi-terminal-glow', 5));
  svg.appendChild(defs);

  // レイヤー (history → active edges → nodes の順で重ね)
  const layerHistory = svgEl('g', { 'data-layer': 'history' });
  const layerActive = svgEl('g', { 'data-layer': 'active-edges' });
  const layerNodes = svgEl('g', { 'data-layer': 'nodes' });
  svg.appendChild(layerHistory);
  svg.appendChild(layerActive);
  svg.appendChild(layerNodes);

  container.appendChild(svg);

  // ---- 入力 dispatch ----
  function dispatch(event: CanvasEvent) {
    if (inputLocked || destroyed) return;
    for (const l of listeners) {
      try {
        l(event);
      } catch (err) {
        // listener の例外で他の listener を止めない
        // eslint-disable-next-line no-console
        console.error('[nagi/canvas] listener threw:', err);
      }
    }
  }

  // ---- 描画本体 ----
  function paint(viewModel: BoardViewModel) {
    if (destroyed) return;

    const positions = resolveNodePositions(viewModel, DEFAULT_VIEW_W, DEFAULT_VIEW_H);

    // 「終端 1 ノード」 判定: 1D 円環で勝利状態 = ノード数 1
    // spec: 「終端 1 ノード (勝利) | 中央大 (半径 56px)、--accent グロー、周囲フェードアウト」
    const isWinTerminal = viewModel.nodes.length === 1;

    // 1) 各レイヤーを再構築 (MVP は full repaint、N<=9 で十分軽い)
    layerHistory.replaceChildren();
    layerActive.replaceChildren();
    layerNodes.replaceChildren();

    // 2) history edges (古いものから先に描画 = 下層)
    //    age が大きい順 = 古い順に sort して下に積む
    const historySorted = [...viewModel.historyEdges].sort((a, b) => b.age - a.age);
    for (const he of historySorted) {
      const [sx, sy] = he.fromPos;
      const [dx, dy] = he.toPos;
      const style = historyEdgeStyle(he.age);

      // self-loop (reason === 'self_loop' かつ from === to 相当): 小さな円で表現
      if (he.reason === 'self_loop' || (sx === dx && sy === dy)) {
        const loop = svgEl('circle', {
          cx: sx,
          cy: sy - NODE_RADIUS * 0.7,
          r: NODE_RADIUS * 0.55,
          fill: 'none',
          stroke: style.stroke,
          'stroke-width': style.strokeWidth,
          'stroke-dasharray': style.dashArray ?? '',
          opacity: style.opacity,
          'data-edge-history-id': he.id,
        });
        layerHistory.appendChild(loop);
        continue;
      }

      const line = svgEl('line', {
        x1: sx,
        y1: sy,
        x2: dx,
        y2: dy,
        stroke: style.stroke,
        'stroke-width': style.strokeWidth,
        'stroke-dasharray': style.dashArray ?? '',
        opacity: style.opacity,
        'data-edge-history-id': he.id,
      });
      layerHistory.appendChild(line);
    }

    // 3) active edges
    for (const ae of viewModel.activeEdges) {
      const sp = positions.get(ae.src);
      const dp = positions.get(ae.dst);
      if (!sp || !dp) continue;

      const style = activeEdgeStyle(ae.highlight);
      const filter =
        style.glow === 'strong'
          ? 'url(#nagi-glow-strong)'
          : style.glow === 'soft'
            ? 'url(#nagi-glow-soft)'
            : '';

      // 同一ノード間の self-loop は applyOperation 後の history 側で扱う前提だが、
      // 念のため (active 中の self-loop もあり得るとして) 描画フォールバック。
      if (ae.src === ae.dst) {
        const loop = svgEl('circle', {
          cx: sp.x,
          cy: sp.y - NODE_RADIUS * 0.7,
          r: NODE_RADIUS * 0.55,
          fill: 'none',
          stroke: style.stroke,
          'stroke-width': style.strokeWidth,
          opacity: style.opacity,
          filter,
          'data-edge-id': ae.id,
          'data-edge-highlight': ae.highlight,
        });
        attachEdgeTap(loop, ae.id);
        layerActive.appendChild(loop);
        continue;
      }

      // 1D 円環の chord (隣接でない 2 ノード) はベジエでわずかに膨らませて描画。
      // 隣接 (= ほぼ等距離) は直線で描いて十分。判定は src-dst 間距離が
      // 円周 1 セグメント (= 2*sin(pi/n)*r) より明確に大きい場合とする。
      // ノード数を取れないので、シンプルに「全 active 辺は弦の可能性あり」 として
      // ベジエに統一しつつ、隣接近似時は bulge=0 と等価になるよう小さく抑える。
      // ヒューリスティック: 距離が NODE_RADIUS*4 以上なら chord、未満なら隣接。
      const dist = Math.hypot(dp.x - sp.x, dp.y - sp.y);
      const isChord = dist > NODE_RADIUS * 4;
      const bulge = isChord ? Math.min(28, dist * 0.18) : 0;

      if (bulge > 0.5) {
        const { cx, cy } = bezierBulgeControl(sp.x, sp.y, dp.x, dp.y, bulge);
        const path = svgEl('path', {
          d: `M ${sp.x} ${sp.y} Q ${cx} ${cy} ${dp.x} ${dp.y}`,
          fill: 'none',
          stroke: style.stroke,
          'stroke-width': style.strokeWidth,
          opacity: style.opacity,
          filter,
          'data-edge-id': ae.id,
          'data-edge-highlight': ae.highlight,
        });
        // タップ判定を太く取るための透明 hit path を兄弟として追加
        const hit = svgEl('path', {
          d: `M ${sp.x} ${sp.y} Q ${cx} ${cy} ${dp.x} ${dp.y}`,
          fill: 'none',
          stroke: 'transparent',
          'stroke-width': 18,
          'data-edge-hit-id': ae.id,
        });
        attachEdgeTap(hit, ae.id);
        layerActive.appendChild(path);
        layerActive.appendChild(hit);
      } else {
        const line = svgEl('line', {
          x1: sp.x,
          y1: sp.y,
          x2: dp.x,
          y2: dp.y,
          stroke: style.stroke,
          'stroke-width': style.strokeWidth,
          opacity: style.opacity,
          filter,
          'data-edge-id': ae.id,
          'data-edge-highlight': ae.highlight,
        });
        const hit = svgEl('line', {
          x1: sp.x,
          y1: sp.y,
          x2: dp.x,
          y2: dp.y,
          stroke: 'transparent',
          'stroke-width': 18,
          'data-edge-hit-id': ae.id,
        });
        attachEdgeTap(hit, ae.id);
        layerActive.appendChild(line);
        layerActive.appendChild(hit);
      }
    }

    // 4) nodes
    for (const node of viewModel.nodes) {
      const p = positions.get(node.id);
      if (!p) continue;

      const isThisTerminal = isWinTerminal; // ノード数 1 のときその 1 個が終端
      const radius = isThisTerminal ? TERMINAL_NODE_RADIUS : NODE_RADIUS;

      // 周囲フェードアウト (勝利時): nodes は dim ではなく強調、active レイヤーは
      // 上位 (controller / end-overlay) で透明化するため renderer 側では未対応。
      // 終端ノード単体には glow を適用。

      const group = svgEl('g', {
        'data-node-id': node.id,
        transform: `translate(${p.x}, ${p.y})`,
      });

      const circle = svgEl('circle', {
        cx: 0,
        cy: 0,
        r: radius,
        fill: 'var(--bg-elev)',
        stroke: isThisTerminal ? 'var(--accent)' : 'var(--border)',
        'stroke-width': isThisTerminal ? 2 : 1,
        filter: isThisTerminal ? 'url(#nagi-terminal-glow)' : '',
      });
      attachNodeTap(circle, node.id);
      group.appendChild(circle);

      // 値 (本体)
      const valueText = svgEl('text', {
        x: 0,
        y: 0,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        'font-size': isThisTerminal ? 28 : 18,
        'font-weight': '500',
        fill: 'var(--text)',
        'pointer-events': 'none',
      });
      valueText.textContent = String(node.value);
      group.appendChild(valueText);

      // プレビュー値 (操作後): 値の隣に薄く「→ 4」
      // spec: 「タップ予測値 — 値の隣に薄く `→ 4` のプレビュー」
      if (
        !isThisTerminal &&
        typeof node.previewValue === 'number' &&
        node.previewValue !== node.value
      ) {
        const preview = svgEl('text', {
          x: 0,
          y: radius + 14,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
          'font-size': 13,
          fill: 'var(--accent-dim)',
          opacity: 0.85,
          'pointer-events': 'none',
        });
        preview.textContent = `→ ${node.previewValue}`;
        group.appendChild(preview);
      }

      layerNodes.appendChild(group);
    }
  }

  // ---- 入力ハンドラ取り付け ----
  function attachEdgeTap(el: SVGElement, edgeId: number) {
    el.style.cursor = 'pointer';
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      dispatch({ type: 'edgeTap', edgeId });
    });
  }

  function attachNodeTap(el: SVGElement, nodeId: number) {
    el.style.cursor = 'pointer';
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      dispatch({ type: 'nodeTap', nodeId });
    });
  }

  // ---- 入力ロック ----
  /**
   * アニメ中の入力を抑止。spec §「入力ロック」:
   *   - アニメ中 (150-600ms): 他入力無効
   *   - 「決定」 タップ直後: 不可逆
   *   - 勝敗 overlay 出現後: ゲーム操作不可
   *
   * 現状 paint は full repaint で同期完了するため自動ロックはしないが、
   * 「次の paint まで動かしてほしくない」 ユースケース向けに公開可能。
   * MVP では update() の前後で短い lock を入れて連打を吸収する。
   */
  function lockBriefly(durationMs: number) {
    inputLocked = true;
    window.setTimeout(() => {
      if (!destroyed) inputLocked = false;
    }, durationMs);
  }

  // ---- 初回描画 ----
  paint(initialViewModel);

  // ---- handle ----
  const handle: CanvasHandle = {
    update(vm: BoardViewModel) {
      if (destroyed) return;
      // 「辺消費 (減算) 150ms」 + 「合流 250ms」 のうち長い方で連打吸収
      // (実アニメーションは MVP では CSS transition + opacity に委ね、
      //  renderer 側の lock はあくまで多重発火防止のセーフティ)
      const isMerge = vm.nodes.length < lastViewModel.nodes.length;
      lockBriefly(isMerge ? T_MID : T_FAST);
      lastViewModel = vm;
      paint(vm);
    },
    destroy() {
      destroyed = true;
      listeners.length = 0;
      container.innerHTML = '';
      container.classList.remove('nagi-canvas-root');
    },
    onEvent(listener) {
      listeners.push(listener);
    },
  };

  return handle;
}

// -------------------------------------------------------------------------
// glow filter builder
// -------------------------------------------------------------------------

function buildGlowFilter(id: string, stdDeviation: number): SVGFilterElement {
  const filter = svgEl('filter', {
    id,
    x: '-50%',
    y: '-50%',
    width: '200%',
    height: '200%',
  });
  const blur = svgEl('feGaussianBlur', {
    in: 'SourceGraphic',
    stdDeviation,
    result: 'blur',
  });
  const merge = svgEl('feMerge');
  const m1 = svgEl('feMergeNode', { in: 'blur' });
  const m2 = svgEl('feMergeNode', { in: 'SourceGraphic' });
  merge.appendChild(m1);
  merge.appendChild(m2);
  filter.appendChild(blur);
  filter.appendChild(merge);
  return filter;
}

// -------------------------------------------------------------------------
// 未使用警告抑止 (T_OVERLAY は将来 end-overlay 連携のためエクスポート候補)
// -------------------------------------------------------------------------
export const _renderTimings = Object.freeze({
  fast: T_FAST,
  mid: T_MID,
  overlay: T_OVERLAY,
});
